import { ProviderUtils } from "./context/useEnv";

export type fetchWebMethods = "GET" | "POST" | "PUT" | "DELETE" | "PATCH";

export interface initFetchWeb {
    method?: fetchWebMethods,
    headers?: any,
    data?: any
}

export const corsHeaders = {
    "Access-Control-Allow-Origin": "*",
    "Access-Control-Allow-Methods": "GET,HEAD,POST,OPTIONS",
    "Access-Control-Max-Age": "86400",
    "Access-Control-Allow-Headers": "Content-Type"
}

export function getValueFromSettled<T>(settledPromise?: PromiseSettledResult<T> ): T | null {
    if (settledPromise && settledPromise.status == "fulfilled") return settledPromise.value
    return null
}

export function generatePlaceholderArrays(amount: number, content: any) {
    return Array.from(Array(amount).keys()).map(item => content)
}

export async function handleApiFetch(
    env: ProviderUtils,
    path = "/",
    init: initFetchWeb | null = null,
    info: Record<string, any> | null = null,
    isJSON = true,
    handleError = true,
) {
    const apiURL = (env.DEV == true && false) ? '://127.0.0.1:50286' : `s://${env["VITE_API_URL"]}`

    const { method, headers, data } = { method: "GET", headers: null, data: null, ...init };

    var fetchMethod = String(method).toUpperCase();
    const allowedMethods = ["GET", "POST", "PUT", "DELETE", "PATCH"]
    if (allowedMethods.indexOf(fetchMethod) == -1) fetchMethod = "GET"

    const body = data != null ? isJSON == true ? { body: JSON.stringify(data) } : { body: data } : null
    const contentType = fetchMethod !== "GET" && (data != null || isJSON == true) ? { "Content-Type": "application/json" } : null

    try {
        const token = info?.token
        let requestHeaders = { ...corsHeaders, ...contentType, ...headers }
        if (token !== undefined) requestHeaders = {
            token, ...requestHeaders
        }

        const request: RequestInit = {
            method: fetchMethod,
            headers: requestHeaders,
            ...body
        }

        const fetchedData = await fetch(`http${apiURL}${path}`, request)

        const response: any = await fetchedData.json();
        if (response["msg"]) {
            if (handleError && env.isClient) {
                console.error("error on path", request, `http${apiURL}${path}`, response)
            }
            return response;
        }

        return response;
    } catch (error) {
    }

    return null;
}

export function getCookie(cookies: string, cname: string) {
    var name = cname + "=";
    var decodedCookie = decodeURIComponent(cookies);
    var ca = decodedCookie.split(';');
    for (var i = 0; i < ca.length; i++) {
        var c = ca[i];
        if (c == undefined) continue;
        while (c.charAt(0) == ' ') {
            c = c.substring(1);
        }
        if (c.indexOf(name) == 0) {
            return c.substring(name.length, c.length);
        }
    }
    return undefined;
}

export async function requestUserInfoWithCookies(cookies: string, env: ProviderUtils) {
    const token = getCookie(cookies, "token")

    const response = await handleApiFetch(env, '/users', {
        method: "POST",
        data: { token }
    })

    if (response == null || response['errorCode']) return null

    return response
}

export async function requestUserInfo(request: Request, env: ProviderUtils) {
    const cookies = request.headers.get("cookie") || ""
    const response = await requestUserInfoWithCookies(cookies,env)

    if (response == null || response['errorCode']) return null

    return response
}

export function covertTimestampISO(timestamp: string) {
    return new Date(timestamp).getTime();
}

export default {
    timeFromTimestamp(timestamp: string | number, hidetime: boolean = false) {
        if (isNaN(parseInt(String(timestamp)))) return "";

        const date = new Date(parseInt(String(timestamp)));
        var time: Record<string, string> = {
            "day": String(date.getDate()).padStart(2, "0"),
            "month": String(date.getMonth() + 1).padStart(2, "0"),
            "year": String(date.getFullYear()),
            "hours": String(date.getHours()).padStart(2, "0"),
            "minutes": String(date.getMinutes()).padStart(2, "0")
        }

        var timeStampCon = time.day + '/' + time.month + '/' + time.year;
        if (hidetime != true) timeStampCon += " " + time.hours + ':' + time.minutes

        return timeStampCon;
    },

    parseStringJSON(string: string) {
        try {
            const jsonData = JSON.parse(string)
            if (jsonData) return jsonData
        } catch (error) { /* empty */ }
        return undefined
    },

    getLastOnline(timestamp: number) {
        const difference = Date.now() - timestamp;

        const time = {
            years: Math.floor(difference / (1000 * 60 * 60 * 24 * 365.25)),
            months: Math.floor(difference / (1000 * 60 * 60 * 24 * 30.4375)),
            weeks: Math.floor(difference / (1000 * 60 * 60 * 24 * 7)),

            days: Math.floor(difference / (1000 * 60 * 60 * 24)),
            hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
            minutes: Math.floor((difference / 1000 / 60) % 60),
            seconds: Math.floor((difference / 1000) % 60),
        };

        function isMultiple(amount: number) {
            if (amount > 1) return 's'
            return ''
        }

        type timeUnits = "years" | "months" | "weeks" | "days" | "hours" | "minutes" | "seconds"
        function getAmount(amount: Record<timeUnits, number>) {
            if (amount.years > 0) return `${amount.years} year${isMultiple(amount.years)}`
            if (amount.months > 0) return `${amount.months} month${isMultiple(amount.months)}`
            if (amount.weeks > 0) return `${amount.weeks} week${isMultiple(amount.weeks)}`
            if (amount.days > 0) return `${amount.days} day${isMultiple(amount.days)}`
            if (amount.hours > 1) return `${amount.hours} hour${isMultiple(amount.hours)}`
            if (amount.minutes > 10) return `${amount.minutes} minute${isMultiple(amount.minutes)}`
        }

        const result = getAmount(time);
        if (result) return `${result} ago`
        if (time.seconds > 0) return `right now`

        return `some time ago`
    }
}

interface userInfo {
    id: number
    username: string
    displayname: string
    badges: number[]
    profile: string
    banner: string
    permissions: Record<string, number>
    balance: number
    token: string
    joindate: number
    lastonline: number
}

declare global {
    interface Window {
      __user_info?: userInfo,
      __client_context?: {
        env: {        
            [key: string]: any
        },
        info?: userInfo
      },
      __env: Record<string, any>
    }
  }